import { hashString } from "./random";
import { computeStamps } from "./stampPath";
import { getTipCanvas } from "./tipTexture";

import type { StampPlacement } from "./stampPath";
import type { BrushParams, EraserMask } from "./types";
import type { ExcalidrawFreeDrawElement } from "../types";

export const isBrushElement = (
  element: ExcalidrawFreeDrawElement,
): element is ExcalidrawFreeDrawElement & { brush: BrushParams } =>
  element.brush != null;

/**
 * canvas-cache padding: worst-case stamp reach from the path, so rotated
 * stamps at param extremes never clip. Stamps are rotated squares (corner
 * reach size/2·√2 ≈ 0.71·size), stampSize can hit ~2× nominal at
 * sizePressure=1, and scatter can offset by up to 1.0·size perpendicular to
 * the path → worst case ≈ (2·0.71 + 1) · size ≈ 2.41·size. 2.5 covers that
 * with headroom.
 */
export const getBrushPadding = (element: ExcalidrawFreeDrawElement): number => {
  const size = element.brushSize ?? 16;
  const maskMax = Math.max(
    0,
    ...(element.eraserMasks ?? []).map((m) => m.size),
  );
  return Math.max(size, maskMax) * 2.5;
};

const stampAll = (
  ctx: CanvasRenderingContext2D,
  stamps: StampPlacement[],
  tip: HTMLCanvasElement,
) => {
  for (const s of stamps) {
    ctx.save();
    ctx.globalAlpha = ctx.globalAlpha * s.alpha;
    ctx.translate(s.x, s.y);
    ctx.rotate(s.rotation);
    ctx.drawImage(tip, -s.size / 2, -s.size / 2, s.size, s.size);
    ctx.restore();
  }
};

/**
 * Punches the element's pixel-eraser strokes out of whatever has already been
 * drawn into `ctx`, in element-local coordinates. Callers must own `ctx`
 * (an offscreen canvas holding just this element) — destination-out on a
 * shared canvas would erase everything underneath.
 *
 * `seed` should be the element's own hash so a mask stamps identically on
 * every render; the per-mask offset keeps two masks from landing the same
 * jitter pattern.
 */
export const applyEraserMasks = (
  ctx: CanvasRenderingContext2D,
  masks: readonly EraserMask[] | undefined,
  seed: number,
): void => {
  if (!masks?.length) {
    return;
  }
  const prev = ctx.globalCompositeOperation;
  const prevAlpha = ctx.globalAlpha;
  ctx.globalCompositeOperation = "destination-out";
  // masks erase fully regardless of the alpha the element was drawn with
  ctx.globalAlpha = 1;
  masks.forEach((mask, i) => {
    // white tip: only alpha matters for destination-out
    const maskTip = getTipCanvas(
      mask.brush,
      mask.size,
      "#ffffff",
      seed ^ (i + 1),
    );
    if (maskTip) {
      stampAll(
        ctx,
        computeStamps(
          mask.points,
          mask.pressures,
          mask.brush,
          mask.size,
          seed ^ (i + 1),
        ),
        maskTip,
      );
    }
  });
  ctx.globalCompositeOperation = prev;
  ctx.globalAlpha = prevAlpha;
};

/**
 * Renders the full textured stroke (stamps + destination-out eraser masks)
 * into a private offscreen canvas, then composites it onto `context` in ONE
 * drawImage — masks can never erase other elements on a shared canvas.
 * Coordinates are element-local (same space as element.points).
 */
export const renderBrushStroke = (
  context: CanvasRenderingContext2D,
  element: ExcalidrawFreeDrawElement,
  strokeColor: string,
  progress?: { start: number; end: number },
): void => {
  const brush = element.brush;
  if (!brush || typeof document === "undefined") {
    return;
  }
  const size = element.brushSize ?? 16;
  const seed = hashString(element.id);

  // bounds over stroke + mask points, padded for tip reach
  const pts = element.points;
  if (pts.length === 0) {
    return;
  }
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  const extend = (p: readonly [number, number] | { 0: number; 1: number }) => {
    minX = Math.min(minX, p[0]);
    minY = Math.min(minY, p[1]);
    maxX = Math.max(maxX, p[0]);
    maxY = Math.max(maxY, p[1]);
  };
  pts.forEach(extend);
  (element.eraserMasks ?? []).forEach((m) => m.points.forEach(extend));
  const pad = getBrushPadding(element);
  const w = Math.min(8192, Math.ceil(maxX - minX + pad * 2)) || 1;
  const h = Math.min(8192, Math.ceil(maxY - minY + pad * 2)) || 1;

  const off = document.createElement("canvas");
  off.width = w;
  off.height = h;
  const octx = off.getContext("2d");
  if (!octx) {
    return;
  }
  octx.translate(pad - minX, pad - minY);

  const tip = getTipCanvas(brush, size, strokeColor, seed);
  if (tip) {
    stampAll(
      octx,
      computeStamps(
        element.points,
        element.pressures,
        brush,
        size,
        seed,
        progress,
      ),
      tip,
    );
  }

  applyEraserMasks(octx, element.eraserMasks, seed);

  context.drawImage(off, minX - pad, minY - pad);
};
